import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTenant } from "../lib/tenant";
import { usePerspective } from "../lib/perspective";
import { useApi, type Tenant } from "../lib/api";
import { NotificationBell } from "./NotificationBell";

type Section =
  | "Überblick"
  | "Programm & Prozesse"
  | "KPIs & Analysen"
  | "KI & Briefings"
  | "Governance & Vertrauen"
  | "Kontext & Hilfe";

const SECTIONS: Section[] = [
  "Überblick",
  "Programm & Prozesse",
  "KPIs & Analysen",
  "KI & Briefings",
  "Governance & Vertrauen",
  "Kontext & Hilfe",
];

type NavItem = {
  to: string;
  label: string;
  hint?: string;
  badge?: string;
  section: Section;
};

const NAV: NavItem[] = [
  { section: "Überblick", to: "/app/start", label: "Single Pane · Alle Systeme", badge: "◎", hint: "Zentrale Startsicht: Module, Dashboards & Fachsysteme aus einem Guss" },
  { section: "Überblick", to: "/app/steuerungssystem", label: "Steuerungssystem", badge: "Neu", hint: "Entscheidungen, Maßnahmen, Wirkung und nächster Messzyklus" },
  { section: "Überblick", to: "/app/steuerkreis", label: "Steuerkreis-Cockpit", badge: "★", hint: "Ampeln, Gate-Matrix, KI-Briefing und Frag die Daten auf einen Blick" },
  { section: "Überblick", to: "/app/kundencockpit", label: "Kundencockpit", badge: "Neu", hint: "Verdichtete Management-/Kundensicht auf einen Blick — beamer-tauglich für Steuerkreis & Kundentermin" },
  { section: "Programm & Prozesse", to: "/app/process-landscape", label: "Prozesslandschaft", badge: "12 Phasen", hint: "59 Teilprozesse, 26 Agenten" },
  { section: "Programm & Prozesse", to: "/app/transformation", label: "Transformation OS", badge: "1&1", hint: "CMB, KEP, Blue und Evidence" },
  { section: "Programm & Prozesse", to: "/app/interventionen", label: "Interventionskatalog", badge: "72", hint: "Situation → richtige Intervention · exakte Datensätze inkl. rechtlicher Vorbehalte" },
  { section: "Programm & Prozesse", to: "/app/meetings", label: "Meeting-Cockpit", badge: "Neu", hint: "KI-Zusammenfassung & Action-Items aus echten Protokollen (grounded)" },
  { section: "Programm & Prozesse", to: "/app/decisions", label: "Entscheidungs-Log", badge: "Neu", hint: "Entscheidungen erfassen, begründen, verknüpfen und nachverfolgen" },
  { section: "Programm & Prozesse", to: "/app/kanban", label: "Kanban-Board", badge: "Neu", hint: "Board aus Arbeitspaketen, Runs, Entscheidungen & eigenen Karten — Drag&Drop für eigene Objekte, OpenProject read-only" },
  { section: "Programm & Prozesse", to: "/app/formulare", label: "Formulare", badge: "Neu", hint: "Generischer Formular-Builder für Erhebungen, Checklisten & Auftragsklärung — Antworten als CSV" },
  { section: "Programm & Prozesse", to: "/app/roadmap", label: "Roadmap", hint: "90-Tage-Agentenplan" },
  { section: "Programm & Prozesse", to: "/app/command-center", label: "Command Center", hint: "Was, warum, was tun" },
  { section: "KPIs & Analysen", to: "/app/metrics", label: "KPI-Katalog", hint: "Definitionen und Metric Contracts" },
  { section: "KPIs & Analysen", to: "/app/candidates", label: "KPI-Kandidaten", hint: "Review vor Anzeige echter Werte" },
  { section: "KPIs & Analysen", to: "/app/trees", label: "Driver Trees", hint: "Wirkungslogik und Ursachen" },
  { section: "KPIs & Analysen", to: "/app/roi", label: "ROI / Monte-Carlo", badge: "Neu", hint: "Business Case und Szenarien" },
  { section: "KPIs & Analysen", to: "/app/benchmarks", label: "Benchmarks", hint: "Marktvergleich und Ranking" },
  { section: "KPIs & Analysen", to: "/app/potential", label: "KI-Potenzialanalyse", badge: "Neu", hint: "Reifegrad, Pain Points, Quick Wins" },
  { section: "KPIs & Analysen", to: "/app/ai-ready-check", label: "AI-Ready-Check", badge: "Neu", hint: "Reifegrad-Assessment: Wie AI-ready ist die Organisation/das Ressort — mit Empfehlungen & Interventionen" },
  { section: "KPIs & Analysen", to: "/app/use-cases", label: "KI Use Cases", hint: "Impact, Readiness, Risiko" },
  { section: "KI & Briefings", to: "/app/briefing-agent", label: "Management-Briefing", badge: "KI", hint: "Echter Agent, lokales Modell, Export" },
  { section: "KI & Briefings", to: "/app/advisor", label: "KI-Berater", hint: "Kontextsensitives Sparring" },
  { section: "KI & Briefings", to: "/app/briefings", label: "Briefings (Archiv)", hint: "Ältere Briefing-Einträge" },
  { section: "Governance & Vertrauen", to: "/app/sources", label: "Datenquellen & Quality", hint: "Anschluss und Vertrauensprüfung" },
  { section: "Governance & Vertrauen", to: "/app/agents", label: "Agent Runs & Evidence", hint: "Replays, Quellen, Status" },
  { section: "Governance & Vertrauen", to: "/app/compliance", label: "Compliance & FRIA", hint: "EU AI Act, Audit, Signatur" },
  { section: "Governance & Vertrauen", to: "/app/achievements", label: "Achievements", hint: "Evidence-basierter Fortschritt" },
  { section: "Kontext & Hilfe", to: "/app/company", label: "Unternehmen", hint: "Mandantenprofil und Beraterkontext" },
  { section: "Kontext & Hilfe", to: "/app/onboarding", label: "Geführte Einführung", badge: "Start", hint: "Branche, Ziele, Datenlage" },
  { section: "Kontext & Hilfe", to: "/app/benachrichtigungen", label: "Erinnerungen & Benachrichtigungen", badge: "Neu", hint: "Proaktive KI: Auslöser, Kanäle und Frequenz einstellen" },
  { section: "Kontext & Hilfe", to: "/app/hilfe", label: "Hilfe & Konzept", badge: "?", hint: "Wie alles zusammenhängt und Glossar" },
];

export const KUNDE_ALLOW = new Set<string>([
  "/app/start",
  "/app/steuerungssystem",
  "/app/kundencockpit",
  "/app/benachrichtigungen",
  "/app/decisions",
  "/app/kanban",
  "/app/formulare",
  "/app/meetings",
  "/app/steuerkreis",
  "/app/briefing-agent",
  "/app/trees",
  "/app/metrics",
  "/app/roadmap",
  "/app/hilfe",
]);

const EXTRA_CRUMB: Record<string, { section: string; label: string; hint?: string }> = {
  "/app/classic": { section: "Überblick", label: "Klassisches Cockpit", hint: "Frühere Startansicht" },
  "/app/cockpit": { section: "Überblick", label: "Cockpit" },
  "/app/agents-live": { section: "Governance & Vertrauen", label: "Agent-Console (live)" },
  "/app/admin/tenant-onboarding": { section: "Admin", label: "Tenant anlegen" },
};

const EXT_LINKS = [
  { href: "/enterprise/", label: "Enterprise-Übersicht", title: "Enterprise-Präsentation (statisch)" },
  { href: "/console.html", label: "Agent-Console", title: "Agent Console v1" },
  { href: "/tools.html", label: "Tools-Explorer", title: "Tools Explorer v1" },
  { href: "/openapi.json", label: "OpenAPI", title: "OpenAPI 3.0 Spec" },
];

const MOBILE_NAV = [
  { to: "/app/steuerungssystem", label: "Steuerung", code: "↻" },
  { to: "/app/steuerkreis", label: "Steuerkreis", code: "★" },
  { to: "/app/briefing-agent", label: "Briefing", code: "KI" },
  { to: "/app/trees", label: "Trees", code: "DT" },
  { to: "/app/roadmap", label: "Roadmap", code: "90" },
  { to: "/app/agents", label: "Runs", code: "AI" },
];
const TECHNICAL_ROLES = new Set(["offline_access", "uma_authorization", "default-roles-kpi-mining", "tenant_user"]);
const ROLE_LABELS: Record<string, string> = {
  ceo: "CEO",
  cfo: "CFO",
  coo: "COO",
  controller: "Controlling",
  tenant_admin: "Tenant Admin",
  platform_admin: "Platform Admin",
};

function visibleRoles(roles: string[]) {
  const businessRoles = roles.filter((r) => !TECHNICAL_ROLES.has(r));
  const executiveRoles = businessRoles.filter((r) => r !== "platform_admin");
  if (executiveRoles.length > 0) return executiveRoles;
  return businessRoles.length > 0 ? businessRoles : roles.filter((r) => !["offline_access", "uma_authorization"].includes(r));
}

function formatRole(role: string) {
  return ROLE_LABELS[role] ?? role.replace(/_/g, " ");
}

function getBrandHost(): string {
  if (typeof window === "undefined") return "ui.ben-e-fit.ai";
  const h = window.location.hostname;
  if (h.includes("medialine")) return "ui.medialine.app";
  if (h.includes("ki-guru")) return "ui.ki-guru.com";
  return "ui.ben-e-fit.ai";
}

/** Kontexthilfe: Routen-Segment -> Anker im Hilfe-Center (/app/hilfe#modul-<id>). */
const HELP_ANCHOR_ALIAS: Record<string, string | null> = {
  classic: "command-center",
  cockpit: "command-center",
  "agents-live": "agents",
  admin: null,
};

function helpLinkFor(pathname: string): string | null {
  if (!pathname.startsWith("/app/")) return null;
  const seg = pathname.slice("/app/".length).split("/")[0];
  if (!seg || seg === "hilfe") return null;
  const alias = HELP_ANCHOR_ALIAS[seg];
  if (alias === null) return "/app/hilfe";
  return `/app/hilfe#modul-${alias ?? seg}`;
}

/** Orientierung: Breadcrumb + Ein-Satz-Kontext "was kann ich hier". */
function Breadcrumb() {
  const { pathname } = useLocation();
  const match = NAV
    .filter((n) => pathname === n.to || pathname.startsWith(n.to + "/"))
    .sort((a, b) => b.to.length - a.to.length)[0];
  const extra = EXTRA_CRUMB[pathname];
  const section = match?.section ?? extra?.section ?? "";
  const label = match?.label ?? extra?.label ?? "Seite";
  const hint = match?.hint ?? extra?.hint;
  const helpTo = helpLinkFor(pathname);
  return (
    <div className="crumbbar">
      <nav className="crumbs" aria-label="Brotkrümelnavigation">
        <Link to="/app/start">KPI Mining</Link>
        {section && (
          <>
            <span className="crumb-sep">/</span>
            <span className="crumb-grp">{section}</span>
          </>
        )}
        <span className="crumb-sep">/</span>
        <span className="crumb-cur" aria-current="page">{label}</span>
        {helpTo && (
          <Link
            to={helpTo}
            className="crumb-help"
            title="Hilfe zu dieser Seite"
            aria-label="Hilfe zu dieser Seite öffnen"
          >
            ?
          </Link>
        )}
      </nav>
      {hint && <span className="crumb-ctx">{hint}</span>}
    </div>
  );
}

function PerspectiveToggle() {
  const { perspective, canChoose, overriding, setPerspective, resetPerspective } = usePerspective();
  if (!canChoose) {
    return <div className="persp-badge kunde" title="Managementsicht (1&1)">Managementsicht</div>;
  }
  return (
    <div className="persp-switch" role="group" aria-label="Sicht umschalten" title="Sicht umschalten (Vorschau der Kundensicht)">
      <button className={perspective === "berater" ? "active" : ""} aria-pressed={perspective === "berater"} onClick={resetPerspective}>Berater</button>
      <button className={perspective === "kunde" ? "active" : ""} aria-pressed={perspective === "kunde"} onClick={() => setPerspective("kunde")}>Kunde</button>
      {overriding && <span className="persp-note">Vorschau</span>}
    </div>
  );
}

function TenantSwitcher() {
  const { effectiveTenantId, setEffectiveTenantId, canSwitch } = useTenant();
  const api = useApi();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!canSwitch) return;
    api.get<Tenant[]>("/tenants").then(setTenants).catch(() => setTenants([]));
  }, [canSwitch]);
  if (!canSwitch) return <div className="tenant-pill">{effectiveTenantId ?? "no-tenant"}</div>;
  const current = tenants.find((t) => t.id === effectiveTenantId);
  return (
    <div className="tenant-switcher">
      <button className="tenant-switcher-btn" onClick={() => setOpen(!open)} aria-expanded={open} title="Tenant wechseln">
        <span className="tenant-pill">{effectiveTenantId ?? "no-tenant"}</span>
        <span className="tenant-name muted small">{current?.display_name ?? "—"}</span>
        <span className="caret">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="tenant-dropdown">
          <div className="tenant-dropdown-hdr">Tenant wechseln ({tenants.length} verfügbar)</div>
          {tenants.map((t) => (
            <button
              key={t.id}
              className={`tenant-option ${t.id === effectiveTenantId ? "active" : ""}`}
              onClick={() => {
                setEffectiveTenantId(t.id);
                setOpen(false);
                window.location.reload();
              }}
            >
              <div className="tenant-option-name">{t.display_name}</div>
              <div className="tenant-option-meta muted small">
                <code>{t.id}</code> · brand {t.brand}
              </div>
            </button>
          ))}
          {tenants.length === 0 && <div className="tenant-option muted small">Keine Tenants gefunden.</div>}
        </div>
      )}
    </div>
  );
}
function SignInButtons() {
  const { authenticated, roles, tenantId, token, logout, email, name, mode } = useAuth();
  if (!authenticated) return null;
  const primaryRole = formatRole(visibleRoles(roles)[0] ?? "user");
  const display = name ?? email ?? primaryRole;
  const caption = mode === "demo" ? "Demo-Profil" : "Keycloak";
  return (
    <div className={`profile-card ${mode === "demo" ? "demo" : ""}`} title={mode === "demo" ? "Demo-Profil" : "Angemeldetes Profil"}>
      <div className="profile-avatar">{display.slice(0, 1).toUpperCase()}</div>
      <div className="profile-copy">
        <strong>{display}</strong>
        <span>{caption} · {tenantId ?? "kein Tenant"}</span>
      </div>
      {(token || mode === "demo") && <button className="btn ghost small" onClick={logout} title={mode === "demo" ? "Demo beenden" : "Abmelden"}>{mode === "demo" ? "Demo beenden" : "Logout"}</button>}
    </div>
  );
}

type NavBadgeCounts = {
  kpi_count?: number;
  kpi_kritisch?: number;
  use_case_count?: number;
  runs_count?: number;
  wp_offen?: number | null;
  prozess_erledigt?: number;
  prozess_gesamt?: number;
};

type ControlBadgeCounts = {
  decision_queue?: { open?: number };
};

/**
 * Sidebar-Badges aus ECHTEN Zählerständen (statt statischer Werbezahlen).
 * Scheitert der Aufruf, bleiben nur die neutralen statischen Badges sichtbar.
 */
function useNavBadges(): Record<string, string> {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const [badges, setBadges] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!effectiveTenantId) return;
    let cancelled = false;
    Promise.allSettled([
      api.get<NavBadgeCounts>("/nav/badges"),
      api.get<ControlBadgeCounts>("/control-system/overview"),
    ])
      .then(([navResult, controlResult]) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        if (navResult.status === "fulfilled") {
          const data = navResult.value;
          if ((data.kpi_count ?? 0) > 0) next["/app/metrics"] = String(data.kpi_count);
          if ((data.use_case_count ?? 0) > 0) next["/app/use-cases"] = String(data.use_case_count);
          if ((data.runs_count ?? 0) > 0) next["/app/agents"] = `${data.runs_count} Runs`;
          if ((data.wp_offen ?? 0) > 0) next["/app/transformation"] = `${data.wp_offen} WP offen`;
          if ((data.kpi_kritisch ?? 0) > 0) next["/app/steuerkreis"] = `${data.kpi_kritisch} kritisch`;
          if ((data.prozess_gesamt ?? 0) > 0) next["/app/process-landscape"] = `${data.prozess_erledigt ?? 0}/${data.prozess_gesamt}`;
        }
        if (controlResult.status === "fulfilled" && (controlResult.value.decision_queue?.open ?? 0) > 0) {
          next["/app/steuerungssystem"] = `${controlResult.value.decision_queue?.open} offen`;
        }
        setBadges(next);
      })
      .catch(() => {
        if (!cancelled) setBadges({});
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveTenantId]);
  return badges;
}

export function Shell() {
  const { roles, mode } = useAuth();
  const { perspective, isBerater } = usePerspective();
  const isDemo = mode === "demo";
  const roleLabel = visibleRoles(roles).slice(0, 3).map(formatRole).join(" · ");
  const launchpadHref = `https://${getBrandHost()}/`;
  const dynamicBadges = useNavBadges();
  const navItems = perspective === "kunde" ? NAV.filter((n) => KUNDE_ALLOW.has(n.to)) : NAV;
  const sections = SECTIONS.reduce<Record<string, NavItem[]>>((acc, s) => {
    acc[s] = navItems.filter((n) => n.section === s);
    return acc;
  }, {});
  return (
    <div className="app">
      <a href="#main-content" className="skip-link">Zum Inhalt springen</a>
      <header className="topbar">
        <a href={launchpadHref} className="brand-link" title="Zurück zum Plattform-Launchpad">
          <span className="brand">KPI Enterprise Mining</span>
          <span className="brand-sub">← Launchpad</span>
        </a>
        <TenantSwitcher />
        <PerspectiveToggle />
        <div className="grow" />
        <nav className="ext-links" aria-label="Weitere Oberflächen">
          {EXT_LINKS.map((l) => (
            <a key={l.href} href={l.href} title={l.title} className="ext-link">{l.label}</a>
          ))}
        </nav>
        <div className="roles" title="Aktive Rollen">{roleLabel}</div>
        <NotificationBell />
        <SignInButtons />
      </header>
      <nav className="mobile-nav" aria-label="Mobile Produktnavigation">
        {MOBILE_NAV.map((item) => (
          <NavLink key={item.to} to={item.to} className={({ isActive }) => `mobile-nav-item ${isActive ? "active" : ""}`}>
            <span>{item.code}</span>
            <strong>{item.label}</strong>
          </NavLink>
        ))}
      </nav>
      <div className="layout">
        <nav className="sidebar" aria-label="Hauptnavigation">
          {perspective === "kunde" && (
            <div className="persp-hint-side">
              <strong>Managementsicht 1&1</strong>
              <span>Verdichtetes Ampel-Cockpit für den Steuerkreis. {isBerater ? "" : "Für Detailsteuerung: Impulse-Beratung."}</span>
            </div>
          )}
          {isDemo && (
            <Link to="/app/onboarding" className="demo-side-banner">
              <strong>Sandbox-Demo</strong>
              <span>Geführter Produktfluss mit Beispieldaten</span>
            </Link>
          )}
          {SECTIONS.map((section) =>
            sections[section].length === 0 ? null : (
              <div className="nav-section" key={section}>
                <div className="nav-section-title">{section}</div>
                {sections[section].map((n) => {
                  const badge = dynamicBadges[n.to] ?? n.badge;
                  return (
                    <NavLink key={n.to} to={n.to} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                      <span>
                        <strong>{n.label}</strong>
                        {n.hint && <small>{n.hint}</small>}
                      </span>
                      {badge && <em title={dynamicBadges[n.to] ? "Echter Zählerstand (live)" : undefined}>{badge}</em>}
                    </NavLink>
                  );
                })}
              </div>
            ),
          )}
          {roles.includes("platform_admin") && perspective === "berater" && (
            <div className="nav-section">
              <div className="nav-section-title">Admin</div>
              <NavLink to="/app/admin/tenant-onboarding" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                <span><strong>Tenant anlegen</strong><small>Admin-Wizard</small></span>
              </NavLink>
            </div>
          )}
        </nav>
        <main className="main" id="main-content" tabIndex={-1}>
          <Breadcrumb />
          <Outlet />
        </main>
      </div>
      <footer className="footer">
        Nichts wird automatisch angewendet · jede Aussage hat einen Beleg · Freigabe durch Menschen · jederzeit rücknehmbar · alles protokolliert{" "}·{" "}<Link to="/app/hilfe">Hilfe &amp; Konzept</Link>
      </footer>
    </div>
  );
}
